import { getMaxZIndex } from 'utils/DomUtils';
import { useEffect, useRef } from 'react';

export const OVERLAY_PORTAL_ID = 'dpc-overlay-portal';

export interface IPortalTargetProps {
  className: string;
}

/**
 * Create a portal target, and add appropriate tracking attributes, especially
 * getting the maximum z-index from the dom plus one, and `ui-front` class
 */
const appendAndMoveToTop = (overlayPortal: HTMLDivElement, props?: IPortalTargetProps) => {
  const body = document.getElementsByTagName('body')[0];
  body.appendChild(overlayPortal);
  const maxZIndex = getMaxZIndex();
  overlayPortal.id = `${OVERLAY_PORTAL_ID}-${maxZIndex}`;
  overlayPortal.setAttribute('style', `z-index: ${maxZIndex.toString()};position: absolute;`);
  // we use ui-front class on the layer in decisions (as jquery-ui also uses it)
  // the position: absolute is also critical to avoiding jquery-ui NaN z-indexes.
  overlayPortal.classList.add('ui-front');
  if (props?.className) {
    overlayPortal.classList.add(props.className);
  }
  return overlayPortal;
};

// TODO args for closing overlay on escape key, etc.
export function useOverlayPortalTarget(isOpen: boolean, props?: IPortalTargetProps) {
  // https://reactjs.org/docs/hooks-effect.html#example-using-hooks-1

  const overlayPortal = useRef<HTMLDivElement>(document.createElement('div'));

  useEffect(() => {
    if (isOpen) {
      appendAndMoveToTop(overlayPortal.current, props);
    }

    const portal = overlayPortal.current;
    return function overlayPortalCleanup() {
      const body = document.getElementsByTagName('body')[0];
      if (portal && body.contains(portal)) {
        body.removeChild(portal);
      }
    };
    // this needs to fire only when something is first displayed,
    // so it doesn't race to the top on every render thereafter, so we're only checking isOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return { overlayPortal: overlayPortal.current };
}
